import _ from 'lodash'
import { Component } from 'react'
import { connect } from 'react-redux'
import { ScrollLock } from '../../LEGACY/elemental'
import Operations from '../../operations'
import Selectors from '../../selectors'
import { LoadingScreen, Step, StepList } from '../generic'
import Modal from '.'
import { CUSTOM_ENTRIES_LIST_ID } from './AddLogModal'
import {
  ModalCategoryItemSelector,
  ModalCategorySelector,
  ModalTitleSelector,
  TodoAssigneeSelector,
  TodoTimeSelector
} from './components'
import {
  BODY_MAPS_CATEGORY,
  categoryHasSelectableLog,
  isCustomEntryVisible
} from './customEntryVisibility'

type TaskCreateModalProps = {
  createItem: (options: any) => void
  onComplete?: () => void
  fields: any
  close: () => void
  isOpen: boolean
  summary: any
  carers: any
  categories: any
  items: any
  residents: any
  /** The synthesised per-category Custom entries, keyed on category id. */
  customEntries: Array<{ category: string; isHidden?: boolean }>
  /** False until the withCustom fetch has completed at least once. */
  customEntriesLoaded: boolean
  loadCustomEntries: () => void
  fetchDailyTodos: () => void
  fetchRecurringTodos: () => void
  showToast: (options: { title: string }) => void
}

export class TaskCreateModal extends Component<TaskCreateModalProps> {
  constructor(props: TaskCreateModalProps) {
    super(props)
    this.checkDescriptionValid = this.checkDescriptionValid.bind(this)
    this.checkSubmitValid = this.checkSubmitValid.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleComplete = this.handleComplete.bind(this)
  }

  checkDescriptionValid() {
    if (!this.props.fields.title || !this.props.fields.description) return false
    return true
  }

  checkSubmitValid() {
    const {
      fields: { assignee, resident }
    } = this.props
    if (
      (!assignee && !resident) ||
      (assignee && !assignee.length) ||
      (resident && !resident.length)
    )
      return false
    return true
  }

  handleSubmit() {
    const { createItem, onComplete } = this.props
    createItem({
      url: 'list/recurring-tasks',
      onComplete: () => {
        this.handleComplete()
        onComplete?.()
      }
    })
  }

  handleComplete = () => {
    const { showToast } = this.props
    showToast({ title: 'Added a To-Do' })
  }

  componentDidUpdate(prevProps: TaskCreateModalProps) {
    // Fetched on open rather than in the app-wide bootstrap: an admin can change
    // the setting between opens, so a stale copy would offer a Custom option
    // that is switched off.
    if (this.props.isOpen && !prevProps.isOpen) {
      this.props.loadCustomEntries()
    }
  }

  render() {
    const {
      close,
      isOpen,
      summary,
      carers,
      categories,
      items,
      residents,
      customEntries,
      customEntriesLoaded,
      ...formProps
    } = this.props
    if (!isOpen) return null
    if ([carers, categories, items, residents, summary].some((item) => !item))
      return <LoadingScreen />
    const { category, title, description } = formProps.fields
    const chosenItems = category && _.filter(items, { category })

    // A Custom to-do is raised through this option: onSelectCustom sets
    // isCustomEntry with no item. Gated on the served entry rather than
    // hardcoded, so a site that switches its Custom log off stops it being
    // offered for new to-dos too. An absent entry counts as hidden.
    // Until the withCustom fetch has completed once, an empty list means
    // "not loaded yet", not "everything hidden" — filtering on it would hide
    // Custom-only categories on first open. Fall back to the pre-CM-4043
    // behaviour (show everything) for that window.
    const showCustom =
      !customEntriesLoaded || isCustomEntryVisible(customEntries, category)

    const pickableCategories = _.reject(categories, {
      itemId: BODY_MAPS_CATEGORY
    })
    // A category with every log off, Custom included, is a dead end here —
    // nothing inside it can be picked for a to-do.
    const selectableCategories = customEntriesLoaded
      ? _.filter(pickableCategories, ({ id }: { id: string }) =>
          categoryHasSelectableLog(items, customEntries, id)
        )
      : pickableCategories
    const {
      text,
      icon: { url, color }
    } = summary
    const validationText =
      title && title.length && description && description.length
        ? 'You need to assign residents or carers'
        : 'Title and Description is required'

    return (
      <div>
        <StepList
          onClose={close}
          backIconColor={color}
          backIcon={url}
          backText={text}
          closeLabel="Cancel"
        >
          <Step
            component={ModalCategorySelector}
            data={{ categories: selectableCategories }}
            {...formProps}
          />
          <Step
            component={ModalCategoryItemSelector}
            data={{ items: chosenItems, categories }}
            showCustom={showCustom}
            {...formProps}
          />
          <Step
            component={ModalTitleSelector}
            isValid={this.checkDescriptionValid}
            validationText={validationText}
            showNextButton
            formId={FORM_ID}
            {...formProps}
          />
          <Step component={TodoTimeSelector} showNextButton {...formProps} />
          <Step
            component={TodoAssigneeSelector}
            data={{ residents, carers }}
            isValid={this.checkSubmitValid}
            onSubmit={this.handleSubmit}
            validationText={validationText}
            buttonGlyph="plus"
            {...formProps}
          />
        </StepList>
        <ScrollLock />
      </div>
    )
  }
}

const mapStateToProps = (state: any, props: any) => ({
  carers: state.data.carers,
  categories: state.data['log-categories'],
  items: state.data['log-category-items'],
  residents: state.data.residents,
  customEntries: _.filter(state.data[CUSTOM_ENTRIES_LIST_ID], { custom: true }),
  customEntriesLoaded: state.data[CUSTOM_ENTRIES_LIST_ID] !== undefined,
  // @ts-ignore
  summary: Selectors.getTodoSummary(state, props)
})

const mapDispatchToProps = (dispatch: any) => ({
  fetchDailyTodos: () => dispatch(Operations.fetchDailyTodos()),
  fetchRecurringTodos: () => dispatch(Operations.fetchRecurringTodos()),
  loadCustomEntries: () =>
    dispatch(
      Operations.loadList(
        CUSTOM_ENTRIES_LIST_ID,
        null,
        true,
        '/api/list/log-category-items?withCustom=true'
      )
    )
})

const FORM_ID = 'createTask'

const wrappedModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(TaskCreateModal)

export default Modal(wrappedModal, FORM_ID)
